import { css } from "@emotion/react"
import { Heading } from "@planningcenter/doxy-web"
import { lowerFirst } from "lodash"
import { useContext } from "react"
import { formattedCents } from "source/giving/utils/money"
import { ServerRenderedProps } from "source/shared/contexts/ServerRenderedProps"
import { useDonationFormContext } from "../hooks/useDonationFormContext"
import { Designation, Frequency } from "../types"
import { frequencyToSummary } from "../utils/frequency"
import { findFund } from "../utils/funds"
import { pushBrowserHistoryState } from "./BrowserNavigationHandler"

type DonationSummaryProps = {
  designations: Designation[]
  totalCents: number
  frequency: Frequency
}

export function DonationSummary({
  designations,
  totalCents,
  frequency,
}: DonationSummaryProps) {
  const {
    config: { funds },
    dispatch,
  } = useDonationFormContext()
  const {
    layout: { organization_name },
  } = useContext(ServerRenderedProps)
  const isSplitDonation = designations.length > 1

  function formatDesignationAmountDestination({
    amount,
    fundId,
  }: Designation): string {
    if (!amount) {
      return ""
    }

    const fundName = findFund(funds, fundId)?.name
    const formattedAmount = formattedCents(parseFloat(amount) * 100) || 0

    if (!fundName) {
      return `${formattedAmount} (no fund specified)`
    }

    return `${formattedAmount} to ${fundName}`
  }

  function getDesignationsTitle() {
    const formattedAmount = formattedCents(totalCents)

    if (frequency.type !== "once") {
      const { prefix, date } = frequencyToSummary(frequency)

      return `${formattedAmount} ${lowerFirst(prefix)} ${date}`
    }

    return isSplitDonation
      ? `${formattedAmount} total`
      : formatDesignationAmountDestination(designations[0])
  }

  return (
    <div
      className="pb-3"
      css={css`
        border-bottom: 1px solid var(--color-tint5);
      `}
    >
      <div className="d-f jc-sb ai-b g-1">
        <div className="f-1">
          <Heading level={3} text={`Give to ${organization_name}`} />
        </div>
        <button
          className="btn link-btn h-a ws-nw"
          onClick={() => {
            dispatch({ type: "SET_FOCUSED_PANE", payload: "left" })
            pushBrowserHistoryState("left")
          }}
          type="button"
        >
          Edit donation
        </button>
      </div>
      <div className="mt-2p">
        <p className="mb-0 c-tint1 fw-500">{getDesignationsTitle()}</p>
        {isSplitDonation &&
          designations.map((designation) => (
            <p key={designation.fundId} className="mb-0 c-tint2">
              {formatDesignationAmountDestination(designation)}
            </p>
          ))}
      </div>
    </div>
  )
}
